import totalAssureLogo from "../../assets/totalassure.png";
import asianPaintsLogo from "../../assets/APlogo.png";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api";
import {
  Home, FolderOpen, PlusCircle,
  FileText, Calendar, CheckCircle2, ClipboardList, Bell,
  Hourglass,
} from "lucide-react";

import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import {
  Card,
  DashboardShell,
  KpiCard,
  SectionHeader,
  StatusBadge,
  palette,
} from "../../components/dashboard/DashboardKit";

// ─── NAV (UNCHANGED) ─────────────────────────────────────────────────────────
const engineerNav = [
  { icon: <Home className="w-5 h-5" />, label: "Dashboard", path: "/engineer" },
  { icon: <FolderOpen className="w-5 h-5" />, label: "My Projects", path: "/engineer/projects" },
  { icon: <PlusCircle className="w-5 h-5" />, label: "Add Project", path: "/engineer/add-project" }
];

// ─── HELPERS (UNCHANGED) ─────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true });
  } catch { return iso; }
}
function normProjectStatus(raw = "") {
  const s = raw.toLowerCase().trim();
  if (s === "completed") return "Completed";
  if (s === "in progress" || s === "in_progress") return "In Progress";
  if (s === "active") return "Active";
  if (s === "rejected") return "Rejected";
  return "Pending";
}
function taskBadgeTone(status = "") {
  const s = status.toLowerCase();
  if (s === "completed") return "progress";
  if (s === "in_progress" || s === "in progress") return "high";
  if (s === "pending") return "urgent";
  return "pending";
}
function approvalBadgeTone(status = "") {
  const s = (status || "").toLowerCase();
  if (s === "pending") return "urgent";
  if (s === "under review") return "high";
  if (s === "waiting approval") return "pending";
  return "pending";
}
function activityIcon(type = "") {
  const t = (type || "").toLowerCase();
  if (t.includes("report")) return <FileText className="w-4 h-4" />;
  if (t.includes("schedule")) return <Calendar className="w-4 h-4" />;
  if (t.includes("task")) return <CheckCircle2 className="w-4 h-4" />;
  if (t.includes("log")) return <ClipboardList className="w-4 h-4" />;
  return <Bell className="w-4 h-4" />;
}

// ─── WEEKLY CHART (UNCHANGED) ────────────────────────────────────────────────
const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_ABBR = { Monday: "Mon", Tuesday: "Tue", Wednesday: "Wed", Thursday: "Thu", Friday: "Fri", Saturday: "Sat", Sunday: "Sun" };
function buildWeeklyData(weeklyActivity = {}) {
  return DAY_ORDER.map((day) => ({ day: DAY_ABBR[day], logs: Number(weeklyActivity[day] ?? 0) }));
}

// ─── PIE CHART (UNCHANGED) ───────────────────────────────────────────────────
const PIE_COLORS = {
  "Active": "#F97316",
  "Completed": "#5C2D91",
  "In Progress": "#3B82F6",
  "Pending": "#A78BFA",
  "Rejected": "#EF4444",
};
function buildPieData(projects = []) {
  const counts = {};
  projects.forEach((p) => {
    const bucket = normProjectStatus(p.status);
    counts[bucket] = (counts[bucket] || 0) + 1;
  });
  return Object.entries(counts).map(([name, value]) => ({ name, value })).filter((d) => d.value > 0);
}
const PieSliceLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
  if (!value) return null;
  const RAD = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.58;
  return (
    <text x={cx + r * Math.cos(-midAngle * RAD)} y={cy + r * Math.sin(-midAngle * RAD)}
      fill="#fff" textAnchor="middle" dominantBaseline="central"
      style={{ fontSize: "11px", fontWeight: 700 }}>{value}</text>
  );
};

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function EngineerDashboard() {
  const navigate = useNavigate();
  const pendingTasksRef = useRef(null);
  const pendingReportsRef = useRef(null);

  // ── STATE ────────────────────────────────────────────────────────────────
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [checkedTasks, setCheckedTasks] = useState([]);
  const [stats, setStats] = useState({
    assignedProjects: 0,
    reportsSubmitted: 0,
    tasksDue: 0,
    completionRate: 0,
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [weeklyActivity, setWeeklyActivity] = useState({});
  const [pendingTasks, setPendingTasks] = useState([]);
  const [pendingApprovalReports, setPendingApprovalReports] = useState([]);

  const userName = localStorage.getItem("name") || "Engineer";
  const userInitials = userName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  // ── API CALLS ─────────────────────────────────────────────────────────────
  useEffect(() => {

    // ── FETCH 1: /engineers/me/projects ─────────────────────────────────────
    // DB: project_assignments table
    //   Columns confirmed from screenshot:
    //     id, project, project_id, manager, engineer, priority, status,
    //     start_date, end_date, is_deleted, created_at, segment, ...
    //
    // Frontend reads: p.status, p.progress, p.project_uid, p.title
    //   MISMATCH 1 — The assignments table has NO progress column.
    //   MISMATCH 2 — project_uid is on the projects table, not assignments.
    //   MISMATCH 3 — title is on the projects table, not assignments.
    //
    // However /engineers/me/projects is a custom route that likely joins
    // projects + project_assignments and returns a normalised shape.
    // We log the FULL raw response so we can see exactly what fields arrive
    // and confirm whether the join is working.
    //
    // FIX: We now accept the raw response and normalise field names so the
    // pie chart always has something to render even if field names differ.
    const fetchProjects = async () => {
      try {
        const res = await API.get("/engineers/me/projects");

        // ── DIAGNOSTIC LOG — bro check browser console for this ──────────
        console.log("[EngineerDashboard] /engineers/me/projects RAW response:", res.data);
        console.log("[EngineerDashboard] first project row sample:", res.data?.projects?.[0]);

        if (res.data?.success) {
          const raw = res.data.projects || [];

          // Normalise each row so the pie chart and KPI always render.
          // We accept both field name variants the join might produce:
          //   title        | project (assignment row stores project name as 'project')
          //   project_uid  | project_id (assignment row project_id = uid string)
          //   status       | status (same in both tables)
          //   progress     | may be missing — default 0
          const normalised = raw.map((p) => ({
            ...p,
            // title: prefer projects.title, fall back to assignment 'project' field
            title: p.title || p.project || "Untitled",
            // project_uid: prefer projects.project_uid, fall back to project_id string
            project_uid: p.project_uid || p.project_id || `PRJ-${p.id}`,
            // status: same field in both tables
            status: p.status || "Pending",
            // progress: only on projects table — default 0 if missing
            progress: Number(p.progress ?? 0),
          }));

          console.log("[EngineerDashboard] normalised projects for pie chart:", normalised.length);
          setAssignedProjects(normalised);
        }
      } catch (err) {
        console.error("[EngineerDashboard] fetchProjects error:", err);
      }
    };

    // ── FETCH 2: /engineers/me/dashboard ────────────────────────────────────
    // This is the main dashboard API.
    // We log the FULL raw response to see exactly what the backend returns.
    //
    // Expected shape (what the frontend was built to consume):
    //   {
    //     success: true,
    //     stats: {
    //       assignedProjects: number,   // count of assignment rows for this engineer
    //       reportsSubmitted: number,   // count from reports table where engineer_id = me
    //       tasksDue:         number,   // count from tasks table where engineer_id = me
    //       completionRate:   number,   // percentage
    //     },
    //     recentActivities: [
    //       { type: string, project_name: string, date: string }
    //     ],
    //     weeklyActivity: {
    //       Monday: number, Tuesday: number, ..., Sunday: number
    //     },
    //     pendingTasks: [
    //       { id, task_name, project_name, due_date, status }
    //     ],
    //     pendingApprovalReports: [
    //       { id, report_name, report_type, project_name, approval_status, upload_date }
    //     ],
    //   }
    //
    // DB columns confirmed from screenshots:
    //   reports:  id, project_id, report_stage, engineer_id, report_type,
    //             title, details, file_url, image_paths, status, approved_by,
    //             approved_at, is_deleted, created_at, reports_library
    //
    //   tasks:    id, title, code, priority, status, due_date,
    //             engineer_id, assigned_by, assigned_role
    //
    //   work_schedule_logs: id, wid, comment, progress_image,
    //                       added_by, added_dt, updated_by, updated_dt
    //
    // KNOWN MISMATCHES between what frontend expects vs DB column names:
    //
    //   pendingTasks[].task_name
    //     → DB column is: tasks.title  (NOT task_name)
    //     → FIX: after fetch, remap task.title → task.task_name
    //
    //   pendingTasks[].project_name
    //     → DB has no project_name on tasks table; must come from a JOIN
    //       with project_assignments or projects via engineer_id
    //     → If backend returns project_name: use it. If not: show "—"
    //
    //   pendingTasks[].due_date
    //     → DB column is: tasks.due_date  ✓ matches
    //
    //   pendingApprovalReports[].report_name
    //     → DB column is: reports.title  (NOT report_name)
    //     → FIX: remap report.title → report.report_name
    //
    //   pendingApprovalReports[].approval_status
    //     → DB column is: reports.status  (NOT approval_status)
    //     → FIX: remap report.status → report.approval_status
    //
    //   pendingApprovalReports[].upload_date
    //     → DB column is: reports.created_at  (NOT upload_date)
    //     → FIX: remap report.created_at → report.upload_date
    //
    //   recentActivities[].type
    //     → No standard DB column; backend likely constructs this string.
    //     → If missing: activityIcon() falls back to "🔔" — safe.
    //
    //   recentActivities[].date
    //     → Backend may send created_at or added_dt — we accept both below.
    //
    //   weeklyActivity object
    //     → Backend counts work_schedule_logs.added_dt grouped by day name.
    //     → If the key names differ (e.g. "Mon" vs "Monday") buildWeeklyData
    //       returns all zeros. We log the raw object so you can verify.
    const fetchDashboard = async () => {
      try {
        const res = await API.get("/engineers/me/dashboard");

        // ── DIAGNOSTIC LOG — bro check browser console for this ──────────
        console.log("[EngineerDashboard] /engineers/me/dashboard RAW response:", res.data);
        console.log("[EngineerDashboard] stats block:", res.data?.stats);
        console.log("[EngineerDashboard] pendingTasks sample:", res.data?.pendingTasks?.[0]);
        console.log("[EngineerDashboard] pendingApprovalReports sample:", res.data?.pendingApprovalReports?.[0]);
        console.log("[EngineerDashboard] recentActivities sample:", res.data?.recentActivities?.[0]);
        console.log("[EngineerDashboard] weeklyActivity keys:", Object.keys(res.data?.weeklyActivity || {}));

        if (res.data?.success) {
          // ── stats (direct — field names match if backend normalises) ──────
          setStats(res.data.stats || {
            assignedProjects: 0,
            reportsSubmitted: 0,
            tasksDue: 0,
            completionRate: 0,
          });

          // ── recentActivities ──────────────────────────────────────────────
          // Accept both 'date' and 'created_at' / 'added_dt' for the timestamp
          const rawActivities = res.data.recentActivities || [];
          const normActivities = rawActivities.map((a) => ({
            ...a,
            // Keep whatever type string the backend sends
            type: a.type || a.activity_type || a.action || "Activity",
            project_name: a.project_name || a.project || "—",
            // Accept any timestamp field
            date: a.date || a.created_at || a.added_dt || a.timestamp || null,
          }));
          setRecentActivities(normActivities);

          // ── weeklyActivity ────────────────────────────────────────────────
          // buildWeeklyData() expects keys: Monday, Tuesday, ..., Sunday
          // If backend sends abbreviated keys (Mon, Tue) we expand them here.
          const ABBR_TO_FULL = {
            Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday",
            Thu: "Thursday", Fri: "Friday", Sat: "Saturday", Sun: "Sunday",
          };
          const rawWeekly = res.data.weeklyActivity || {};
          const normWeekly = {};
          Object.entries(rawWeekly).forEach(([key, val]) => {
            // If key is already full name use it; if abbreviated expand it
            const fullKey = ABBR_TO_FULL[key] || key;
            normWeekly[fullKey] = Number(val) || 0;
          });
          console.log("[EngineerDashboard] normWeekly after expansion:", normWeekly);
          setWeeklyActivity(normWeekly);

          // ── pendingTasks ──────────────────────────────────────────────────
          // DB: tasks.title (not task_name), tasks.due_date ✓, tasks.status ✓
          // Backend may already alias title→task_name. We handle both.
          const rawTasks = res.data.pendingTasks || [];
          const normTasks = rawTasks.map((t) => ({
            ...t,
            // task_name: prefer backend alias, fall back to DB column 'title'
            task_name: t.task_name || t.title || "Unnamed Task",
            project_name: t.project_name || t.project || "—",
            due_date: t.due_date || t.dueDate || null,
            status: t.status || "Pending",
          }));
          console.log("[EngineerDashboard] normTasks:", normTasks.length, normTasks[0]);
          setPendingTasks(normTasks);

          // ── pendingApprovalReports ────────────────────────────────────────
          // DB: reports.title (not report_name), reports.status (not approval_status),
          //     reports.created_at (not upload_date)
          // Backend may already alias these. We handle both.
          const rawReports = res.data.pendingApprovalReports || [];
          const normReports = rawReports.map((r) => ({
            ...r,
            // report_name: prefer backend alias, fall back to DB 'title'
            report_name: r.report_name || r.title || "Untitled Report",
            report_type: r.report_type || "—",
            project_name: r.project_name || r.project || "—",
            // approval_status: prefer backend alias, fall back to DB 'status'
            approval_status: r.approval_status || r.status || "Pending",
            // upload_date: prefer backend alias, fall back to DB 'created_at'
            upload_date: r.upload_date || r.created_at || null,
          }));
          console.log("[EngineerDashboard] normReports:", normReports.length, normReports[0]);
          setPendingApprovalReports(normReports);
        }
      } catch (err) {
        console.error("[EngineerDashboard] fetchDashboard error:", err);
      }
    };

    fetchProjects();
    fetchDashboard();
  }, []);

  // ── DERIVED — unchanged ───────────────────────────────────────────────────
  const pieData = buildPieData(assignedProjects);
  const weeklyData = buildWeeklyData(weeklyActivity);
  const toggleTask = (id) =>
    setCheckedTasks((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);

  // ─────────────────────────────────────────────────────────────────────────
  // UI IS COMPLETELY UNCHANGED FROM HERE DOWN
  // Only the data normalisation above was changed.
  // ─────────────────────────────────────────────────────────────────────────
return (
    <>
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');
      * { font-family: 'Montserrat', sans-serif !important; }
    `}</style>
    <DashboardShell
      portal="Engineer Portal"
      roleLabel="ENGINEER"
      title={
        <div className="flex items-center gap-4">
          <img src={totalAssureLogo} alt="SmartAssure" className="h-14 md:h-16 w-auto object-contain" />
            <img
                src={asianPaintsLogo}
                alt="Asian Paints"
                className="h-12 md:h-14 w-auto object-contain"
              />
        </div>
      }
      subtitle=""
      navItems={engineerNav}
      stats={[
        { label: "Assigned", value: String(stats.assignedProjects).padStart(2, "0"), className: "text-ap-violetLight" },
        { label: "Tasks", value: String(stats.tasksDue).padStart(2, "0"), className: "text-ap-orange" },
        { label: "Reports", value: String(stats.reportsSubmitted).padStart(2, "0"), className: "text-ap-red" },
      ]}
      user={{ initials: userInitials, name: userName, role: "Engineer" }}
    >

      {/* ══ KPI CARDS ══ */}
      <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Assigned Projects"
          value={String(stats.assignedProjects).padStart(2, "0")}
          icon={<FolderOpen className="w-7 h-7" />}
          badge="Active"
          tone="violet"
          onClick={() => navigate("/engineer/projects")}
        />
        <KpiCard
          label="Reports Submitted"
          value={String(stats.reportsSubmitted).padStart(2, "0")}
          icon={<ClipboardList className="w-7 h-7" />}
          badge="This Month"
          tone="red"
          onClick={() => pendingReportsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
        />
        <KpiCard
          label="Tasks Due"
          value={String(stats.tasksDue).padStart(2, "0")}
          icon={<Hourglass className="w-7 h-7" />}
          badge="Urgent"
          tone="orange"
          onClick={() => pendingTasksRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
        />
        <KpiCard label="Completion Rate" value={`${stats.completionRate}%`} icon={<CheckCircle2 className="w-7 h-7" />} badge="On Track" tone="gold" />
      </div>

      {/* ══ ROW 2: PIE CHART + RECENT ACTIVITY ══ */}
      <div className="mt-4 grid gap-3.5 xl:grid-cols-[3fr_2fr]">

        <Card bar="gold">
          <div className="p-5 sm:p-6">
            <SectionHeader
              title="Project Status Overview"
              subtitle={`${assignedProjects.length} total assigned project${assignedProjects.length === 1 ? "" : "s"}`}
            />
            {pieData.length > 0 ? (
              <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:gap-8 mt-2">
                <div className="w-full sm:w-[280px] shrink-0">
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={pieData} cx="50%" cy="50%"
                        innerRadius={64} outerRadius={100}
                        paddingAngle={3} dataKey="value"
                        labelLine={false} label={PieSliceLabel}
                        startAngle={90} endAngle={-270}
                      >
                        {pieData.map((entry) => (
                          <Cell key={entry.name} fill={PIE_COLORS[entry.name] || "#A78BFA"} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, name) => [`${value} project${value !== 1 ? "s" : ""}`, name]}
                        contentStyle={{ fontSize: "12px", borderRadius: "10px", border: "1px solid #EDE6F7" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-3 sm:flex-col sm:flex-nowrap sm:gap-3 w-full">
                  {pieData.map((entry) => (
                    <div key={entry.name}
                      className="flex items-center gap-3 rounded-[12px] border border-ap-border bg-ap-surface px-4 py-3 flex-1 min-w-[120px]">
                      <span className="h-3 w-3 rounded-full shrink-0"
                        style={{ backgroundColor: PIE_COLORS[entry.name] || "#A78BFA" }} />
                      <div>
                        <div className="text-xl font-bold text-ap-dark leading-none">{entry.value}</div>
                        <div className="text-[10px] text-ap-muted mt-0.5">{entry.name}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex h-[220px] items-center justify-center text-[12px] text-ap-muted">
                Loading project data…
              </div>
            )}
          </div>
        </Card>

        <Card bar="violet">
          <div className="p-5 sm:p-6">
            <SectionHeader title="Recent Activity" subtitle="Latest actions" />
            <div className="mt-2 space-y-2 max-h-[260px] overflow-y-auto pr-1">
              {recentActivities.length > 0 ? (
                recentActivities.map((act, i) => (
                  <div key={i}
                    className="flex items-start gap-3 rounded-[12px] border border-ap-border bg-ap-surface px-3 py-2.5 hover:bg-ap-violet/10 transition">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-ap-violetLight text-base">
                      {activityIcon(act.type)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[12px] font-semibold text-ap-dark">{act.type}</div>
                      <div className="truncate text-[11px] text-ap-muted">{act.project_name || "—"}</div>
                      <div className="text-[10px] text-ap-faint mt-0.5">{fmtDateTime(act.date)}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex h-[180px] items-center justify-center text-[12px] text-ap-muted">
                  No recent activity.
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* ══ ROW 3: WEEKLY ACTIVITY + PENDING TASKS ══ */}
      <div className="mt-4 grid gap-3.5 xl:grid-cols-[3fr_2fr]">

        <Card>
          <div className="p-5 sm:p-6">
            <SectionHeader
              title="Weekly Activity"
              subtitle="Work log entries per day"
              action={
                <span className="flex items-center gap-1.5 text-[11px] text-ap-muted">
                  <span className="h-2.5 w-2.5 rounded-sm bg-ap-violet inline-block" />
                  Work Logs
                </span>
              }
            />
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={weeklyData} margin={{ top: 8, right: 4, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id="logsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#5C2D91" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#5C2D91" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(92,45,145,0.08)" vertical={false} />
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                <YAxis axisLine={false} tickLine={false} allowDecimals={false} tick={{ fontSize: 11 }} width={26} />
                <Tooltip
                  formatter={(v) => [v, "Work Logs"]}
                  contentStyle={{ fontSize: "11px", borderRadius: "10px", border: "1px solid #EDE6F7" }}
                />
                <Area type="monotone" dataKey="logs" name="Work Logs"
                  stroke={palette.violet || "#5C2D91"} strokeWidth={2.5}
                  fill="url(#logsGrad)"
                  dot={{ r: 4, fill: palette.violet || "#5C2D91", strokeWidth: 0 }}
                  activeDot={{ r: 6, strokeWidth: 0 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

<Card bar="red" >
          <div ref={pendingTasksRef} className="p-5 sm:p-6">
            <SectionHeader
              title="Pending Tasks"
              subtitle={`${pendingTasks.length} task${pendingTasks.length === 1 ? "" : "s"} remaining`}
            />
            <div className="mt-2 space-y-2 max-h-[260px] overflow-y-auto pr-1">
              {pendingTasks.length > 0 ? (
                pendingTasks.map((task) => {
                  const uid = task.id ?? String(task.task_name);
                  const isDone = checkedTasks.includes(uid);
                  return (
                    <div key={uid}
                      className={`flex items-center gap-2.5 rounded-[12px] border border-ap-border px-3 py-2.5 transition ${isDone ? "bg-ap-bg opacity-55" : "bg-ap-surface hover:bg-ap-violet/10"
                        }`}>
                      <button type="button" onClick={() => toggleTask(uid)}
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-[6px] border text-[10px] transition ${isDone ? "border-ap-violet bg-ap-violet text-white" : "border-ap-faint text-transparent"
                          }`}>✓</button>
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-ap-violetLight">
  {isDone ? <CheckCircle2 className="w-4 h-4 text-ap-violet" /> : <ClipboardList className="w-4 h-4 text-ap-violet" />}
</div>
                      <div className="min-w-0 flex-1">
                        <div className={`truncate text-[12px] font-semibold text-ap-dark ${isDone ? "line-through" : ""}`}>
                          {task.task_name || "Unnamed Task"}
                        </div>
                        <div className="truncate text-[10px] text-ap-muted">
                          {task.project_name || "—"} · Due {fmtDate(task.due_date)}
                        </div>
                      </div>
                      <StatusBadge label={task.status} status={taskBadgeTone(task.status)} />
                    </div>
                  );
                })
              ) : (
                <div className="flex h-[160px] items-center justify-center text-[12px] text-ap-muted">
                  All tasks completed! 🎉
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* ══ ROW 4: PENDING APPROVAL REPORTS ══ */}
      <div className="mt-4">
        <Card bar="mixed">
          <div ref={pendingReportsRef} className="p-5 sm:p-6">
            <SectionHeader
              title="Pending Approval Reports"
              subtitle={`${pendingApprovalReports.length} report${pendingApprovalReports.length === 1 ? "" : "s"} awaiting review`}
            />
            {pendingApprovalReports.length > 0 ? (
              <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {pendingApprovalReports.map((report) => (
                  <div key={report.id}
                    className="flex items-start gap-3 rounded-[14px] border border-ap-border bg-ap-surface p-4 hover:bg-ap-violet/10 transition">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-ap-violetLight">
  <FileText className="w-5 h-5 text-ap-violet" />
</div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-bold text-ap-dark">
                        {report.report_name || report.report_type || "Untitled Report"}
                      </div>
                      <div className="truncate text-[11px] text-ap-muted mt-0.5">
                        {report.project_name || "—"}
                      </div>
                      <div className="text-[10px] text-ap-faint mt-0.5">
                        {report.report_type || "—"}
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <StatusBadge label={report.approval_status} status={approvalBadgeTone(report.approval_status)} />
                        <span className="text-[10px] text-ap-muted whitespace-nowrap">
                          {fmtDate(report.upload_date)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex h-[100px] items-center justify-center text-[12px] text-ap-muted mt-2">
                No reports pending approval.
              </div>
            )}
          </div>
        </Card>
      </div>

   </DashboardShell>
    </>
  );
}